export type Via = 'dt5' | 'dt6';

/** Single-value answers (yesno, single-select). */
export type AnswerValue =
  | 'yes'
  | 'no'
  // asilo_fecha
  | 'dontRemember'
  // antecedentes
  | 'cancelable'
  | 'vigentes'
  | 'noSure'
  // pasaporte
  | 'caducado'
  // tiempo_espana (consolida en_espana + permanencia_5m + llegada)
  | 'not_in_spain'
  | 'lt_5m'
  | '5_12m'
  | '1_3y'
  | 'plus_3y'
  // estado_excluyente — opciones que bloquean elegibilidad si están marcadas
  | 'residencia_vigente'
  | 'otro_procedimiento'
  | 'ucrania'
  | 'prohibicion_entrada'
  | 'orden_publico'
  | 'asilo_previo'
  // familia_espana
  | 'hijos_menores'
  | 'hijos_discapacidad'
  | 'conyuge'
  | 'padres'
  | 'ninguno'
  // supuesto (DT6)
  | 'A'
  | 'B'
  | 'C'
  // documentacion_laboral (DT6)
  | 'contrato'
  | 'oferta'
  | 'autonomo'
  | 'cotizaciones'
  // acreditacion_vulnerabilidad (DT6)
  | 'irregular'
  | 'ssociales'
  | 'ong'
  | 'padron'
  | 'citas_medicas'
  | 'envio_dinero'
  | 'envio_cartas'
  | 'carnet_biblioteca'
  | 'tarjeta_transporte'
  | 'facturas'
  | 'otros_docs'
  | 'na';

/** A stored answer is either a single value or a list of values (multiselect). */
export type StoredAnswer = AnswerValue | AnswerValue[];

export interface Question {
  id: string;
  type: 'yesno' | 'select' | 'multiselect';
  /** i18n key under questions.{via}.{id} */
  i18nKey: string;
  /** For select / multiselect type questions */
  options?: { value: AnswerValue; i18nLabel: string }[];
  /** Condition to show this question — evaluated against current answers */
  showIf?: (answers: Record<string, StoredAnswer>) => boolean;
}

export interface BlockingCondition {
  questionId: string;
  /**
   * Condition to mark the answer as blocking. Either an exact value match
   * (single answer) or a predicate evaluated against the stored answer.
   */
  blockingAnswer?: AnswerValue;
  blockingPredicate?: (answer: StoredAnswer | undefined) => boolean;
}

// ─── Reusable option presets ───────────────────────────────────

const tiempoEspanaOptions = [
  { value: 'not_in_spain' as AnswerValue, i18nLabel: 'quiz.opt_not_in_spain' },
  { value: 'lt_5m' as AnswerValue, i18nLabel: 'quiz.opt_lt_5m' },
  { value: '5_12m' as AnswerValue, i18nLabel: 'quiz.opt_5_12m' },
  { value: '1_3y' as AnswerValue, i18nLabel: 'quiz.opt_1_3y' },
  { value: 'plus_3y' as AnswerValue, i18nLabel: 'quiz.opt_plus_3y' },
];

const yesNoDontRememberOptions = [
  { value: 'yes' as AnswerValue, i18nLabel: 'quiz.yes' },
  { value: 'no' as AnswerValue, i18nLabel: 'quiz.no' },
  { value: 'dontRemember' as AnswerValue, i18nLabel: 'quiz.opt_dontRemember' },
];

const antecedentesOptions = [
  { value: 'no' as AnswerValue, i18nLabel: 'quiz.no' },
  { value: 'cancelable' as AnswerValue, i18nLabel: 'quiz.opt_cancelable' },
  { value: 'vigentes' as AnswerValue, i18nLabel: 'quiz.opt_vigentes' },
  { value: 'noSure' as AnswerValue, i18nLabel: 'quiz.opt_noSure' },
];

const pasaporteOptions = [
  { value: 'yes' as AnswerValue, i18nLabel: 'quiz.yes' },
  { value: 'caducado' as AnswerValue, i18nLabel: 'quiz.opt_caducado' },
  { value: 'no' as AnswerValue, i18nLabel: 'quiz.no' },
];

const estadoExcluyenteDt5Options = [
  { value: 'residencia_vigente' as AnswerValue, i18nLabel: 'quiz.opt_residencia_vigente' },
  { value: 'otro_procedimiento' as AnswerValue, i18nLabel: 'quiz.opt_otro_procedimiento' },
  { value: 'ucrania' as AnswerValue, i18nLabel: 'quiz.opt_ucrania' },
  { value: 'prohibicion_entrada' as AnswerValue, i18nLabel: 'quiz.opt_prohibicion_entrada' },
];

const estadoExcluyenteDt6Options = [
  ...estadoExcluyenteDt5Options,
  { value: 'orden_publico' as AnswerValue, i18nLabel: 'quiz.opt_orden_publico' },
  { value: 'asilo_previo' as AnswerValue, i18nLabel: 'quiz.opt_asilo_previo' },
];

const familiaOptions = [
  { value: 'hijos_menores' as AnswerValue, i18nLabel: 'quiz.opt_hijos_menores' },
  { value: 'hijos_discapacidad' as AnswerValue, i18nLabel: 'quiz.opt_hijos_discapacidad' },
  { value: 'conyuge' as AnswerValue, i18nLabel: 'quiz.opt_conyuge' },
  { value: 'padres' as AnswerValue, i18nLabel: 'quiz.opt_padres' },
];

const documentacionLaboralOptions = [
  { value: 'contrato' as AnswerValue, i18nLabel: 'quiz.opt_contrato' },
  { value: 'oferta' as AnswerValue, i18nLabel: 'quiz.opt_oferta' },
  { value: 'autonomo' as AnswerValue, i18nLabel: 'quiz.opt_autonomo' },
  { value: 'cotizaciones' as AnswerValue, i18nLabel: 'quiz.opt_cotizaciones' },
  { value: 'no' as AnswerValue, i18nLabel: 'quiz.no' },
];

const vulnerabilidadOptions = [
  { value: 'irregular' as AnswerValue, i18nLabel: 'quiz.opt_irregular' },
  { value: 'ssociales' as AnswerValue, i18nLabel: 'quiz.opt_ssociales' },
  { value: 'ong' as AnswerValue, i18nLabel: 'quiz.opt_ong' },
  { value: 'padron' as AnswerValue, i18nLabel: 'quiz.opt_padron' },
  { value: 'citas_medicas' as AnswerValue, i18nLabel: 'quiz.opt_citas_medicas' },
  { value: 'envio_dinero' as AnswerValue, i18nLabel: 'quiz.opt_envio_dinero' },
  { value: 'envio_cartas' as AnswerValue, i18nLabel: 'quiz.opt_envio_cartas' },
  { value: 'carnet_biblioteca' as AnswerValue, i18nLabel: 'quiz.opt_carnet_biblioteca' },
  { value: 'tarjeta_transporte' as AnswerValue, i18nLabel: 'quiz.opt_tarjeta_transporte' },
  { value: 'facturas' as AnswerValue, i18nLabel: 'quiz.opt_facturas' },
  { value: 'otros_docs' as AnswerValue, i18nLabel: 'quiz.opt_otros_docs' },
  { value: 'na' as AnswerValue, i18nLabel: 'quiz.opt_na' },
];

const TIEMPO_NO_BLOCKING: AnswerValue[] = ['5_12m', '1_3y', 'plus_3y'];

// ─── DT5 Questions (consolidado: 6 preguntas) ─────────────────

export const dt5Questions: Question[] = [
  {
    id: 'asilo_fecha',
    type: 'select',
    i18nKey: 'asilo_fecha',
    options: yesNoDontRememberOptions,
  },
  {
    id: 'tiempo_espana',
    type: 'select',
    i18nKey: 'tiempo_espana',
    options: tiempoEspanaOptions,
  },
  {
    id: 'estado_excluyente',
    type: 'multiselect',
    i18nKey: 'estado_excluyente',
    options: estadoExcluyenteDt5Options,
  },
  {
    id: 'antecedentes',
    type: 'select',
    i18nKey: 'antecedentes',
    options: antecedentesOptions,
  },
  {
    id: 'familia',
    type: 'multiselect',
    i18nKey: 'familia',
    options: familiaOptions,
  },
  { id: 'expediente_expulsion', type: 'yesno', i18nKey: 'expediente_expulsion' },
];

export const dt5BlockingConditions: BlockingCondition[] = [
  { questionId: 'asilo_fecha', blockingAnswer: 'no' },
  {
    questionId: 'tiempo_espana',
    blockingPredicate: (a) =>
      a === 'not_in_spain' || a === 'lt_5m' || (Array.isArray(a) && a.length === 0),
  },
  {
    questionId: 'estado_excluyente',
    blockingPredicate: (a) => Array.isArray(a) && a.length > 0,
  },
];

// ─── DT6 Questions (consolidado: 8 preguntas) ─────────────────

export const dt6Questions: Question[] = [
  {
    id: 'tiempo_espana',
    type: 'select',
    i18nKey: 'tiempo_espana',
    options: tiempoEspanaOptions,
  },
  {
    id: 'pasaporte',
    type: 'select',
    i18nKey: 'pasaporte',
    options: pasaporteOptions,
  },
  {
    id: 'estado_excluyente',
    type: 'multiselect',
    i18nKey: 'estado_excluyente',
    options: estadoExcluyenteDt6Options,
  },
  {
    id: 'antecedentes',
    type: 'select',
    i18nKey: 'antecedentes',
    options: antecedentesOptions,
  },
  {
    id: 'supuesto',
    type: 'select',
    i18nKey: 'supuesto',
    options: [
      { value: 'A', i18nLabel: 'quiz.optionA' },
      { value: 'B', i18nLabel: 'quiz.optionB' },
      { value: 'C', i18nLabel: 'quiz.optionC' },
    ],
  },
  {
    id: 'documentacion_laboral',
    type: 'select',
    i18nKey: 'documentacion_laboral',
    options: documentacionLaboralOptions,
    showIf: (answers) => answers.supuesto !== 'C',
  },
  {
    id: 'acreditacion_vulnerabilidad',
    type: 'select',
    i18nKey: 'acreditacion_vulnerabilidad',
    options: vulnerabilidadOptions,
    showIf: (answers) => answers.supuesto === 'C',
  },
  {
    id: 'familia',
    type: 'multiselect',
    i18nKey: 'familia',
    options: familiaOptions,
  },
  { id: 'expediente_expulsion', type: 'yesno', i18nKey: 'expediente_expulsion' },
];

export const dt6BlockingConditions: BlockingCondition[] = [
  {
    questionId: 'tiempo_espana',
    blockingPredicate: (a) =>
      a === 'not_in_spain' || a === 'lt_5m',
  },
  { questionId: 'pasaporte', blockingAnswer: 'no' },
  {
    questionId: 'estado_excluyente',
    blockingPredicate: (a) => Array.isArray(a) && a.length > 0,
  },
];

export function getQuestions(via: Via): Question[] {
  return via === 'dt5' ? dt5Questions : dt6Questions;
}

export function getVisibleQuestions(
  via: Via,
  answers: Record<string, StoredAnswer>
): Question[] {
  return getQuestions(via).filter((q) => !q.showIf || q.showIf(answers));
}

export function getBlockingConditions(via: Via): BlockingCondition[] {
  return via === 'dt5' ? dt5BlockingConditions : dt6BlockingConditions;
}

export function tiempoEspanaIsPositive(answer: StoredAnswer | undefined): boolean {
  return typeof answer === 'string' && (TIEMPO_NO_BLOCKING as string[]).includes(answer);
}
